import type { BackupEntry } from '@/mocks/saGlobalBackupCenter';
import { statusMeta, typeMeta } from '@/mocks/saGlobalBackupCenter';

interface BackupDetailModalProps {
  entry: BackupEntry | null;
  onClose: () => void;
  onRestore?: (entry: BackupEntry) => void;
  onDownload?: (entry: BackupEntry) => void;
  onDelete?: (entry: BackupEntry) => void;
}

export default function BackupDetailModal({ entry, onClose, onRestore, onDownload, onDelete }: BackupDetailModalProps) {
  if (!entry) return null;

  const st = statusMeta[entry.status];
  const tp = typeMeta[entry.type];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-background-200">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-background-50 border-b border-background-200 px-5 py-4 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${st.bg} flex items-center justify-center`}>
              <i className={`${st.icon} ${st.color} text-lg`}></i>
            </div>
            <div>
              <h3 className="text-foreground-900 font-bold text-sm">{entry.id}</h3>
              <p className="text-foreground-400 text-[10px]">{entry.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-background-200 flex items-center justify-center cursor-pointer transition-colors">
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5">
          {/* Status & Type badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg ${st.bg} ${st.color} text-[10px] font-semibold`}>
              <i className={`${st.icon} text-[10px]`}></i>
              {st.label}
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-background-200 text-foreground-600 text-[10px] font-medium">
              <i className={`${tp.icon} text-[10px]`}></i>
              {tp.label}
            </span>
            {entry.encryption && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-600 text-[10px] font-medium">
                <i className="ri-lock-line text-[10px]"></i>
                Encriptado
              </span>
            )}
          </div>

          {/* Info grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[10px] mb-0.5">Tamaño</p>
              <p className="text-foreground-800 text-sm font-bold">{entry.sizeLabel}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[10px] mb-0.5">Duración</p>
              <p className="text-foreground-800 text-sm font-bold">{entry.durationLabel}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[10px] mb-0.5">Compresión</p>
              <p className="text-foreground-800 text-sm font-bold font-mono">{entry.compression}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[10px] mb-0.5">Fecha</p>
              <p className="text-foreground-800 text-xs font-semibold">
                {new Date(entry.date).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}
              </p>
              <p className="text-foreground-400 text-[10px]">
                {new Date(entry.date).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </p>
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[10px] mb-0.5">Servidor</p>
              <p className="text-foreground-800 text-xs font-semibold font-mono">{entry.server}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[10px] mb-0.5">Base de Datos</p>
              <p className="text-foreground-800 text-xs font-semibold font-mono">{entry.database}</p>
            </div>
          </div>

          {/* Database details */}
          <div className="bg-background-100 rounded-xl p-4">
            <p className="text-foreground-400 text-[10px] font-medium mb-2 uppercase tracking-wider">Información de la Base de Datos</p>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <p className="text-foreground-400 text-[10px]">Tablas</p>
                <p className="text-foreground-800 text-sm font-bold">{entry.tablesCount}</p>
              </div>
              <div>
                <p className="text-foreground-400 text-[10px]">Iniciado por</p>
                <p className="text-foreground-800 text-xs font-semibold">{entry.initiatedBy}</p>
              </div>
              <div>
                <p className="text-foreground-400 text-[10px]">Checksum</p>
                <p className="text-foreground-800 text-[10px] font-mono truncate" title={entry.checksum}>{entry.checksum.substring(0, 12)}...</p>
              </div>
            </div>
          </div>

          {/* Files included */}
          {entry.filesIncluded.length > 0 && (
            <div>
              <p className="text-foreground-400 text-[10px] font-medium mb-2 uppercase tracking-wider">Archivos Incluidos</p>
              <div className="flex flex-wrap gap-1.5">
                {entry.filesIncluded.map((f) => (
                  <span key={f} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-background-200 text-foreground-600 text-[10px] font-mono">
                    <i className="ri-file-line text-[10px]"></i>
                    {f}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Result */}
          <div className={`rounded-xl p-4 ${entry.status === 'failed' ? 'bg-red-50 border border-red-200' : entry.status === 'in_progress' ? 'bg-amber-50 border border-amber-200' : 'bg-background-100'}`}>
            <p className="text-foreground-400 text-[10px] font-medium mb-1 uppercase tracking-wider">Resultado</p>
            <p className={`text-xs ${entry.status === 'failed' ? 'text-red-700' : entry.status === 'in_progress' ? 'text-amber-700' : 'text-foreground-700'}`}>
              {entry.result}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-background-200">
            {entry.status === 'success' && (
              <>
                <button
                  onClick={() => onRestore?.(entry)}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-50 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line text-sm"></i>
                  Restaurar
                </button>
                <button
                  onClick={() => onDownload?.(entry)}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-background-200 text-foreground-700 text-xs font-semibold hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-download-2-line text-sm"></i>
                  Descargar
                </button>
              </>
            )}
            {entry.status === 'failed' && (
              <button
                onClick={() => onRestore?.(entry)}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-amber-50 text-amber-700 text-xs font-semibold hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-restart-line text-sm"></i>
                Reintentar
              </button>
            )}
            <button
              onClick={() => onDelete?.(entry)}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-red-50 text-red-600 text-xs font-semibold hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-line text-sm"></i>
              Eliminar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}